import React from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import RequiredBadge from '@core/components/RequiredBadge';
import { FiFileText } from 'react-icons/fi';
import { District } from '../types/district';
import { useAssignedTemplates } from '@features/report/template/hooks/useAssignedTemplates';

type Props = {
  show: boolean;
  onHide: () => void;
  district: District | null;
};

const ViewDistrictAssignedTemplatesModal: React.FC<Props> = ({ show, onHide, district }) => {
  const { departmentsWithTemplates, loading, error } = useAssignedTemplates({
    districtId: district?.district_id,
    enabled: show && !!district?.district_id,
  });

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <FiFileText aria-hidden />
          Report Templates{district ? ` — ${district.district_name}` : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        ) : error ? (
          <p className="text-danger mb-0">{error}</p>
        ) : departmentsWithTemplates.length === 0 ? (
          <p className="text-muted mb-0">No report templates have been assigned to this district yet.</p>
        ) : (
          <div className="d-flex flex-column gap-3">
            {departmentsWithTemplates.map((dept) => (
              <div key={dept.department_id}>
                <div className="fw-semibold mb-2">{dept.department_name}</div>
                <div className="border rounded">
                  {dept.templates.map((template, index) => (
                    <div
                      key={template.report_template_id}
                      className={`px-3 py-2 ${index > 0 ? 'border-top' : ''}`}
                    >
                      <div className="d-flex align-items-start justify-content-between gap-2">
                        <div>
                          <div className="fw-medium">{template.report_template_name}</div>
                          {template.report_template_description && (
                            <small className="text-muted">{template.report_template_description}</small>
                          )}
                        </div>
                        {template.is_required && (
                          <RequiredBadge title="This report is required" aria-label="Required report" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ViewDistrictAssignedTemplatesModal;
